import React, { useEffect, useState, useCallback } from "react";
import { UserPlus, Pencil, Trash2, Power, Users as UsersIcon } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { InlineLoader } from "@/components/Spinner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { timeLabel, dateLabel } from "@/lib/status";

const EMPTY = { name: "", username: "", password: "", active: true };

export default function Workers() {
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/workers").then((r) => setWorkers(r.data)).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setEditing(null); setForm(EMPTY); setModalOpen(true); };
  const openEdit = (w) => { setEditing(w); setForm({ name: w.name, username: w.username, password: "", active: w.active }); setModalOpen(true); };

  const save = async () => {
    if (!form.name.trim() || !form.username.trim()) { toast.error("Name and username are required"); return; }
    if (!editing && !form.password) { toast.error("Password is required for new workers"); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/workers/${editing.id}`, form);
        toast.success("Worker updated");
      } else {
        await api.post("/workers", form);
        toast.success("Worker added");
      }
      setModalOpen(false);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (w) => {
    try {
      await api.put(`/workers/${w.id}`, { name: w.name, username: w.username, active: !w.active });
      toast.success(`${w.name} ${w.active ? "deactivated" : "activated"}`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/workers/${deleteTarget.id}`);
      toast.success("Worker deleted");
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="space-y-5 bb-fade-up">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight">Workers</h1>
          <p className="text-sm text-stone-500">{workers.length} worker{workers.length === 1 ? "" : "s"}</p>
        </div>
        <Button onClick={openAdd} data-testid="add-worker-btn" className="gap-2 rounded-xl font-semibold">
          <UserPlus className="h-4 w-4" /> Add Worker
        </Button>
      </div>

      {loading ? (
        <InlineLoader />
      ) : workers.length === 0 ? (
        <div className="flex flex-col items-center gap-2 rounded-2xl border border-dashed border-stone-300 bg-white py-16 text-stone-400">
          <UsersIcon className="h-9 w-9" strokeWidth={1.8} />
          <p className="text-sm font-medium">No workers yet. Add your first worker.</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-stone-200 bg-white">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] text-sm" data-testid="workers-table">
              <thead>
                <tr className="border-b border-stone-200 bg-stone-50 text-left text-xs uppercase tracking-wide text-stone-500">
                  <th className="px-4 py-3 font-semibold">Name</th>
                  <th className="px-4 py-3 font-semibold">Username</th>
                  <th className="px-4 py-3 font-semibold">Status</th>
                  <th className="px-4 py-3 font-semibold">Last login</th>
                  <th className="px-4 py-3 font-semibold">Actions today</th>
                  <th className="px-4 py-3 text-right font-semibold">Manage</th>
                </tr>
              </thead>
              <tbody>
                {workers.map((w) => (
                  <tr key={w.id} className="border-b border-stone-100 last:border-0" data-testid={`worker-row-${w.username}`}>
                    <td className="px-4 py-3 font-semibold">{w.name}</td>
                    <td className="px-4 py-3 text-stone-600">{w.username}</td>
                    <td className="px-4 py-3">
                      <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${w.active ? "bg-emerald-50 text-emerald-700" : "bg-stone-100 text-stone-500"}`}>
                        {w.active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-stone-500">{w.last_login ? `${dateLabel(w.last_login.slice(0, 10))} ${timeLabel(w.last_login)}` : "Never"}</td>
                    <td className="px-4 py-3 font-bold">{w.actions_today}</td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1">
                        <button onClick={() => openEdit(w)} data-testid={`edit-worker-${w.username}`} className="flex h-8 w-8 items-center justify-center rounded-lg text-stone-500 hover:bg-stone-100" title="Edit">
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button onClick={() => toggleActive(w)} data-testid={`toggle-worker-${w.username}`} className={`flex h-8 w-8 items-center justify-center rounded-lg hover:bg-stone-100 ${w.active ? "text-amber-600" : "text-emerald-600"}`} title={w.active ? "Deactivate" : "Activate"}>
                          <Power className="h-4 w-4" />
                        </button>
                        <button onClick={() => setDeleteTarget(w)} data-testid={`delete-worker-${w.username}`} className="flex h-8 w-8 items-center justify-center rounded-lg text-red-500 hover:bg-red-50" title="Delete">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent data-testid="worker-modal">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Worker" : "Add Worker"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-sm font-semibold">Worker name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="worker-name-input" placeholder="e.g. Nikola" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-semibold">Username</Label>
              <Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} data-testid="worker-username-input" placeholder="e.g. nikola" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-semibold">{editing ? "New password (leave blank to keep)" : "Password"}</Label>
              <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="worker-password-input" placeholder="••••••••" />
            </div>
            <div className="flex items-center justify-between rounded-xl bg-stone-50 px-4 py-3">
              <div>
                <p className="text-sm font-semibold">Active</p>
                <p className="text-xs text-stone-500">Inactive workers cannot log in.</p>
              </div>
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="worker-active-switch" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)} data-testid="worker-cancel">Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="worker-save">{saving ? "Saving…" : "Save Worker"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="confirm-delete-worker">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleteTarget?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the worker account. Their past action history is preserved in reports.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-worker-btn">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
